import React, { useEffect, useState } from "react";
import { Box, Grid, Stack, Typography, Divider, Alert } from "@mui/material";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { Surface, SectionTitle, Kpi, StatusChip } from "components/ui";
import { T, inrShort, statusTone } from "theme/tokens";
import * as api from "../api";

/**
 * The machine-readable codes the wallet gate returns, in the words an admin
 * would use. Falling back to the raw code is deliberate — a new gate should
 * show up as something unfamiliar rather than be silently swallowed.
 */
const BLOCKED_REASONS = {
  REQUEST_IN_PROGRESS: "Withdrawal already in progress",
  NO_BANK_DETAILS: "No bank details",
  BANK_UNVERIFIED: "Bank not verified",
  BANK_VERIFICATION_PENDING: "Bank verification pending",
  BANK_UNDER_REVIEW: "Bank under review",
  BANK_VERIFICATION_FAILED: "Bank verification failed",
  BANK_COOLDOWN: "Bank recently changed — in cooling-off",
  BELOW_MINIMUM: "Below the minimum withdrawal",
  PHARMACY_NOT_FOUND: "Pharmacy not found",
};

function Field({ label, value }) {
  return (
    <Box sx={{ mb: 1.4 }}>
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 600,
          color: T.subtle,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", color: T.ink, wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function OverviewTab({ pharmacyId, pharmacy }) {
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([api.getStats(pharmacyId), api.getWallet(pharmacyId)])
      .then(([statsRes, walletRes]) => {
        if (!alive) return;
        setStats(statsRes.stats);
        setWallet(walletRes);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.response?.data?.message || "Could not load statistics.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [pharmacyId]);

  const trend = (stats?.trend || []).map((d) => ({ date: d._id, count: d.count }));

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {/* ── KPIs ───────────────────────────────────────────────────────── */}
      <Surface>
        <Grid container divider>
          {[
            { label: "Total orders", value: stats?.totalOrders ?? 0 },
            {
              label: "Revenue",
              value: inrShort(stats?.revenue),
              sub: `${stats?.paidOrders ?? 0} paid`,
              tone: T.success,
            },
            { label: "Accepted", value: stats?.accepted ?? 0, tone: T.success },
            { label: "Rejected", value: stats?.rejected ?? 0, tone: T.error },
            { label: "Missed", value: stats?.missing ?? 0, tone: T.warning },
          ].map((kpi, index) => (
            <Grid
              item
              xs={6}
              md
              key={kpi.label}
              sx={{
                borderRight: { md: index < 4 ? `1px solid ${T.borderSoft}` : "none" },
                borderBottom: { xs: `1px solid ${T.borderSoft}`, md: "none" },
              }}
            >
              <Kpi {...kpi} loading={loading} />
            </Grid>
          ))}
        </Grid>
      </Surface>

      <Grid container spacing={2}>
        {/* ── Trend ────────────────────────────────────────────────────── */}
        <Grid item xs={12} lg={7}>
          <Surface sx={{ p: 2, height: "100%" }}>
            <SectionTitle>Orders · last 30 days</SectionTitle>
            {trend.length ? (
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                    <defs>
                      <linearGradient id="phOrders" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={T.secondary} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={T.secondary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: T.subtle }}
                      tickLine={false}
                      axisLine={false}
                      // 30 labels will not fit; show roughly every fifth.
                      interval={Math.max(0, Math.floor(trend.length / 6) - 1)}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: T.subtle }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <RTooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: `1px solid ${T.border}`,
                        fontSize: "0.8rem",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke={T.secondary}
                      strokeWidth={2}
                      fill="url(#phOrders)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography sx={{ color: T.muted, fontSize: "0.85rem", py: 6, textAlign: "center" }}>
                {loading ? "Loading…" : "No orders in the last 30 days."}
              </Typography>
            )}
          </Surface>
        </Grid>

        {/* ── Status breakdown ─────────────────────────────────────────── */}
        <Grid item xs={12} lg={5}>
          <Surface sx={{ p: 2, height: "100%" }}>
            <SectionTitle>Orders by status</SectionTitle>
            <Stack spacing={1}>
              {Object.entries(stats?.statusCounts || {}).length ? (
                Object.entries(stats.statusCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([status, count]) => (
                    <Stack
                      key={status}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <StatusChip status={status} />
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: statusTone(status),
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {count}
                      </Typography>
                    </Stack>
                  ))
              ) : (
                <Typography sx={{ color: T.muted, fontSize: "0.85rem" }}>
                  {loading ? "Loading…" : "No orders yet."}
                </Typography>
              )}
            </Stack>
          </Surface>
        </Grid>
      </Grid>

      {/* ── Wallet ─────────────────────────────────────────────────────── */}
      {/* Read straight from the ledger — the same figures the pharmacy sees
          in its own app. This panel used to show `PharmacyOrders.totalBalance`
          behind a "ledger pending" notice; that counter credits on order
          ACCEPT at the gross price and is never debited on payout, so it read
          far higher than anything the pharmacy could actually withdraw. */}
      <Surface sx={{ p: 2 }}>
        <SectionTitle
          action={
            wallet?.canWithdraw === false && wallet?.blockedReason ? (
              <StatusChip
                tone={T.warning}
                label={BLOCKED_REASONS[wallet.blockedReason] || wallet.blockedReason}
              />
            ) : null
          }
        >
          Wallet
        </SectionTitle>

        <Grid container>
          {[
            {
              label: "Withdrawable",
              value: wallet?.balance?.display,
              sub: wallet?.canWithdraw ? "can be withdrawn now" : "withdrawal blocked",
              tone: T.success,
            },
            {
              label: "Locked",
              value: wallet?.locked?.display,
              sub: wallet?.openRequest
                ? `request ${wallet.openRequest.requestId}`
                : "no open request",
              tone: wallet?.locked?.paise ? T.warning : T.muted,
            },
            {
              label: "Lifetime earned",
              value: wallet?.lifetimeEarned?.display,
              sub: "credited on delivery",
              tone: T.info,
            },
            {
              label: "Lifetime paid out",
              value: wallet?.lifetimeWithdrawn?.display,
              sub: "settled to bank",
              tone: T.muted,
            },
          ].map((kpi, index) => (
            <Grid
              item
              xs={6}
              md={3}
              key={kpi.label}
              sx={{
                borderRight: { md: index < 3 ? `1px solid ${T.borderSoft}` : "none" },
                borderBottom: { xs: `1px solid ${T.borderSoft}`, md: "none" },
              }}
            >
              <Kpi
                label={kpi.label}
                value={kpi.value ?? "—"}
                sub={kpi.sub}
                tone={kpi.tone}
                loading={loading}
              />
            </Grid>
          ))}
        </Grid>

        {wallet?.openRequest && (
          <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, fontSize: "0.83rem" }}>
            A withdrawal of <b>{wallet.openRequest.amount?.display}</b> is{" "}
            {wallet.openRequest.status} — that money is in Locked, not Withdrawable.
          </Alert>
        )}
      </Surface>

      {/* ── Profile at a glance ────────────────────────────────────────── */}
      <Surface sx={{ p: 2 }}>
        <SectionTitle>At a glance</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Field label="Owner" value={pharmacy?.fullName} />
            <Field label="Business phone" value={pharmacy?.businessPhone} />
            <Field label="Owner phone" value={pharmacy?.ownerPhone} />
            <Field label="Email" value={pharmacy?.email} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label="GST No" value={pharmacy?.gstNo} />
            <Field label="Medical licence" value={pharmacy?.medicalLicenseNo} />
            <Field label="Drug licence" value={pharmacy?.drugLicenseNo} />
            <Field label="Business reg. no" value={pharmacy?.bussinessRegNo} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label="Address" value={pharmacy?.address} />
            <Field label="Pincode" value={pharmacy?.pincode} />
            <Field
              label="Coordinates"
              value={
                pharmacy?.coordinates
                  ? `${pharmacy.coordinates.lat.toFixed(5)}, ${pharmacy.coordinates.lng.toFixed(5)}`
                  : "Not set"
              }
            />
            <Field
              label="Bank"
              value={
                pharmacy?.bankDetails?.accountNumber
                  ? `${pharmacy.bankDetails.accountNumber} · ${pharmacy.bankDetails.ifscCode || ""}`
                  : "Not added"
              }
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: "0.75rem", color: T.subtle }}>
          Registered {pharmacy?.dateOfRegistration || "—"} · Licence valid to{" "}
          {pharmacy?.dateOfMedicalLicense || "—"}
        </Typography>
      </Surface>
    </Stack>
  );
}
